//#region IMPORT
import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Dialog, Button } from '../../../../Components/MUIs';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  VerifiedUser as VerifiedUserIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Schedule as ScheduleIcon,
  Draw as DrawIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
//#endregion

const GREY_BG = '#f4f6f8';
const BORDER_DASH = '1px dashed #c4cdd5';

const FLOW_GRID_SX = {
  display: 'grid',
  gridTemplateColumns:
    'minmax(0, 1fr) 22px minmax(0, 1fr) 22px minmax(0, 1fr) 22px minmax(0, 1fr)',
  alignItems: 'stretch',
  width: '100%',
  minWidth: 720,
};

const FLOW_STEP_SX = {
  minWidth: 0,
  height: '100%',
  minHeight: 142,
  p: 1.25,
  borderRadius: 2,
  border: '1px solid #e0e6ed',
  bgcolor: '#fff',
  display: 'flex',
  flexDirection: 'column',
};

const FLOW_SIGN_STEP_SX = {
  ...FLOW_STEP_SX,
  minHeight: 154,
};

const SETUP_STEP_BODY_SX = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 56,
  minWidth: 0,
};

const ADD_APPROVER_BOX_SX = {
  flex: 1,
  width: '100%',
  minHeight: 56,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.25,
  border: BORDER_DASH,
  borderRadius: 1.5,
  cursor: 'pointer',
  color: 'primary.main',
  bgcolor: '#fafbfc',
  '&:hover': { bgcolor: '#f0f7ff' },
};

const PERSON_BLOCK_SX = {
  minHeight: 36,
  mb: 0.5,
  minWidth: 0,
};

const SIGN_PLACEHOLDER_SX = {
  flexShrink: 0,
  p: 0.75,
  border: BORDER_DASH,
  borderRadius: 1,
  minHeight: 44,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: '#fafafa',
  textAlign: 'center',
};

const STEP_ACTION_SX = {
  mt: 'auto',
  flexShrink: 0,
  minHeight: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
};

function FlowGrid({ children }) {
  return (
    <Box sx={{ overflowX: 'auto', pb: 0.5, mx: -0.5, px: 0.5 }}>
      <Box sx={FLOW_GRID_SX}>{children}</Box>
    </Box>
  );
}

function FlowArrowCell() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#90a4ae',
        alignSelf: 'center',
      }}
    >
      <ArrowForwardIcon sx={{ fontSize: 22 }} />
    </Box>
  );
}

function PersonLine({ primary, secondary, primaryTitle, secondaryTitle }) {
  return (
    <Box sx={PERSON_BLOCK_SX}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700 }}
        noWrap
        title={primaryTitle || primary}
      >
        {primary || '—'}
      </Typography>
      {secondary != null && secondary !== '' && (
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          title={secondaryTitle || secondary}
          sx={{ display: 'block', mt: 0.25 }}
        >
          {secondary}
        </Typography>
      )}
    </Box>
  );
}

function SignImagePreview({ signPath, alt }) {
  if (!signPath || !String(signPath).trim()) {
    return null;
  }
  return (
    <img
      src={
        Global.file.useUrl + 'files/' + Commons.EncodeUrl(String(signPath).trim())
      }
      alt={alt || '서명'}
      style={{
        maxWidth: '100%',
        maxHeight: 36,
        objectFit: 'contain',
      }}
    />
  );
}

function SignStatusBody({
  name,
  signed,
  signPath,
  signLabel,
  emptyLabel = '미지정',
}) {
  const hasSignImage = signed && signPath && String(signPath).trim();

  return (
    <>
      <Box sx={SETUP_STEP_BODY_SX}>
        {name ? (
          <>
            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={name}>
              {name}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <StatusBadge done={signed} />
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Chip
              size="small"
              icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
              label={emptyLabel}
              sx={{ bgcolor: '#eceff1', color: '#546e7a', fontWeight: 600 }}
            />
          </Box>
        )}
      </Box>
      <Box sx={SIGN_PLACEHOLDER_SX}>
        {hasSignImage ? (
          <SignImagePreview signPath={signPath} alt={`${name || ''} 서명`} />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {signLabel}
          </Typography>
        )}
      </Box>
    </>
  );
}

function StepOrderHeader({ order, role, stepLabel }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
      <Avatar
        sx={{
          width: 24,
          height: 24,
          bgcolor: '#1565c0',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {order}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={700}
          sx={{ display: 'block', lineHeight: 1.2 }}
        >
          {stepLabel}
        </Typography>
        <Typography variant="body2" fontWeight={700} noWrap>
          {role}
        </Typography>
      </Box>
    </Box>
  );
}

function FlowGuideBanner() {
  return (
    <Box
      sx={{
        mb: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        bgcolor: '#e3f2fd',
        border: '1px solid #bbdefb',
      }}
    >
      <Typography variant="body2" sx={{ color: '#0d47a1', fontWeight: 600 }}>
        결재 진행 순서: 신청자 → 중간 결재자(1→2) → 최종 결재자
      </Typography>
      <Typography variant="caption" sx={{ color: '#1565c0', display: 'block' }}>
        중간 결재자는 최대 2명까지만 추가할 수 있습니다.
      </Typography>
    </Box>
  );
}

function FormatDepFromUserInfo(userInfo) {
  if (!userInfo) return '';
  const parts = [
    userInfo.depNm1,
    userInfo.depNm2,
    userInfo.depNm3,
    userInfo.depNm4,
  ].filter((p) => p && String(p).trim());
  return parts.join(' ') || '';
}

function StatusBadge({ done, labelDone = '서명완료', labelWait = '대기중' }) {
  if (done) {
    return (
      <Chip
        size="small"
        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
        label={labelDone}
        sx={{
          bgcolor: '#e8f5e9',
          color: '#2e7d32',
          fontWeight: 600,
          '& .MuiChip-icon': { color: '#2e7d32' },
        }}
      />
    );
  }
  return (
    <Chip
      size="small"
      icon={<HourglassEmptyIcon sx={{ fontSize: 16 }} />}
      label={labelWait}
      sx={{
        bgcolor: '#eceff1',
        color: '#546e7a',
        fontWeight: 600,
        '& .MuiChip-icon': { color: '#78909c' },
      }}
    />
  );
}

/**
 * 결재 상신 팝업 — 중간 결재자 최대 2명, 최종 결재자 1명 선택 UI
 * 상신 클릭 후 신청자 서명 단계에서 서명 확인 시 onSubmit(payload) 호출
 */
function TRA_CUSTOM_02_ApprovalModal({
  open,
  onClose,
  userInfo,
  intermediateApprovers = [],
  finalApprover = null,
  approvalSignStatus = null,
  onRemoveIntermediate,
  onOpenIntermediatePicker,
  onOpenFinalPicker,
  onSubmit,
  onSaveLine,
  validateBeforeSignStep,
}) {
  const [step, setStep] = useState('form');
  const [signError, setSignError] = useState(false);
  const signatureRef = useRef(null);

  const drafterName = userInfo?.userNm || userInfo?.name || '';
  const drafterDep = FormatDepFromUserInfo(userInfo);
  const drafterTitle =
    userInfo?.position || userInfo?.rank || userInfo?.title || '';

  const applicantSigned = Boolean(approvalSignStatus?.applicantSigned);
  const midSignedById = approvalSignStatus?.midSignedById || {};
  const midSignPathById = approvalSignStatus?.midSignPathById || {};
  const applicantSignPath = approvalSignStatus?.applicantSignPath || '';
  const savedFinalSignId = String(approvalSignStatus?.finalSignId ?? '').trim();
  const currentFinalApproverId =
    finalApprover?.id != null ? String(finalApprover.id).trim() : '';
  const finalSignBelongsToCurrentApprover =
    Boolean(currentFinalApproverId) &&
    Boolean(savedFinalSignId) &&
    currentFinalApproverId === savedFinalSignId;
  const finalSigned =
    Boolean(approvalSignStatus?.finalSigned) &&
    finalSignBelongsToCurrentApprover;
  const finalSignPath = finalSignBelongsToCurrentApprover
    ? approvalSignStatus?.finalSignPath || ''
    : '';

  useEffect(() => {
    if (!open) return;
    setStep('form');
    setSignError(false);
  }, [open]);

  useEffect(() => {
    if (open && step === 'sign' && signatureRef.current) {
      signatureRef.current.clear();
      setSignError(false);
    }
  }, [open, step]);

  const HandleClose = () => {
    setStep('form');
    setSignError(false);
    onClose?.();
  };

  const HandleClickSubmit = () => {
    if (!validateBeforeSignStep?.()) {
      return;
    }
    setStep('sign');
  };

  const HandleSaveLine = async () => {
    if (!validateBeforeSignStep?.()) {
      return;
    }
    const result = await onSaveLine?.();
    if (result !== false) {
      HandleClose();
    }
  };

  const submitButtonLabel = applicantSigned ? '재상신' : '상신';

  const HandleClearSignature = () => {
    signatureRef.current?.clear();
    setSignError(false);
  };

  const HandleConfirmSignature = async () => {
    const sig = signatureRef.current;
    if (!sig || sig.isEmpty()) {
      setSignError(true);
      return;
    }
    setSignError(false);
    const signatureDataUrl = sig.toDataURL('image/png');
    const decodedData = atob(signatureDataUrl.split(',')[1]);
    const byteNumbers = new Array(decodedData.length);
    for (let i = 0; i < decodedData.length; i++) {
      byteNumbers[i] = decodedData.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/png' });
    const file = new File(
      [blob],
      `approval_sign_${Date.now()}.png`,
      { type: 'image/png' }
    );
    const result = await onSubmit?.({
      signatureDataUrl,
      signatureFile: file,
    });

    if (result !== false) {
      HandleClose();
    }
  };

  const dialogTitle =
    step === 'sign' ? '신청자 서명' : '결재 상신';

  return (
    <Dialog
      open={open}
      onClose={HandleClose}
      title={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            pr: 0,
          }}
        >
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {dialogTitle}
          </Typography>
          <IconButton aria-label="닫기" size="small" onClick={HandleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      }
      useCloseButton={false}
      useSaveButton={false}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          width: 'min(960px, 96vw)',
          maxWidth: '96vw',
        },
      }}
      contentSx={{
        pt: 1,
        pb: 2,
        px: 2,
        maxHeight: 'min(88vh, 900px)',
        overflow: 'auto',
      }}
      content={
        <Box>
          {step === 'sign' ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DrawIcon color="primary" />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  아래 영역에{' '}
                  <Typography component="span" color="primary" fontWeight={700}>
                    {drafterName || '신청자'}
                  </Typography>
                  님의 서명을 직접 작성해 주세요.
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                터치펜·마우스로 서명합니다. 잘못 그렸다면 &quot;지우기&quot;로
                비운 뒤 다시 작성할 수 있습니다.
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  p: 1,
                  bgcolor: '#fafafa',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    '& .sigCanvas': { display: 'block', width: '100% !important' },
                  }}
                >
                  <SignatureCanvas
                    ref={signatureRef}
                    penColor="black"
                    minWidth={2}
                    maxWidth={4}
                    throttle={16}
                    canvasProps={{
                      className: 'sigCanvas',
                      style: {
                        height: Commons.IsMobile() ? 200 : 220,
                        width: '100%',
                        borderRadius: 8,
                        backgroundColor: '#E4EFED',
                        border: '1px solid #bdbdbd',
                        touchAction: 'none',
                      },
                    }}
                    onEnd={() => setSignError(false)}
                  />
                  <Typography
                    sx={{
                      fontSize: '2.5rem',
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none',
                      color: 'rgba(0, 0, 0, 0.08)',
                      fontWeight: 600,
                      maxWidth: '85%',
                      textAlign: 'center',
                      wordBreak: 'keep-all',
                    }}
                  >
                    {drafterName}
                  </Typography>
                </Box>
              </Box>
              {signError && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  서명을 입력한 뒤 확인을 눌러 주세요.
                </Typography>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1.5,
                  mt: 3,
                  pt: 2,
                  borderTop: '1px solid #eceff1',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  name="지우기"
                  variant="outlined"
                  onClick={HandleClearSignature}
                  sx={{
                    minWidth: 88,
                    borderColor: '#cfd8dc',
                    color: 'text.primary',
                  }}
                />
                <Button
                  name="이전"
                  variant="outlined"
                  onClick={() => setStep('form')}
                  sx={{
                    minWidth: 88,
                    bgcolor: '#fff',
                    borderColor: '#cfd8dc',
                    color: 'text.primary',
                  }}
                />
                <Button
                  name="확인"
                  variant="contained"
                  onClick={HandleConfirmSignature}
                  sx={{
                    minWidth: 88,
                    bgcolor: '#1565c0',
                    color: '#fff',
                    '&:hover': { bgcolor: '#0d47a1' },
                  }}
                />
              </Box>
            </Box>
          ) : (
            <>
          {/* 결재선 설정 */}
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mb: 1.5,
              }}
            >
              <SettingsIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                결재선 설정
              </Typography>
            </Box>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: GREY_BG,
                borderRadius: 2,
                border: '1px solid #e0e6ed',
              }}
            >
              <FlowGuideBanner />
              <FlowGrid>
                <Paper elevation={0} sx={{ ...FLOW_STEP_SX, bgcolor: '#f8fbff' }}>
                  <StepOrderHeader order={1} stepLabel="1단계" role="신청자" />
                  <Box sx={SETUP_STEP_BODY_SX}>
                    <PersonLine
                      primary={drafterName}
                      secondary={
                        [drafterDep, drafterTitle].filter(Boolean).join(' / ') ||
                        '본인 (자동)'
                      }
                    />
                  </Box>
                  <Box sx={STEP_ACTION_SX}>
                    <Chip size="small" label="상신자" sx={{ fontWeight: 600 }} />
                  </Box>
                </Paper>

                <FlowArrowCell />

                {[0, 1].map((slot) => {
                  const p = intermediateApprovers[slot];
                  return (
                    <React.Fragment key={`mid-setup-${slot}`}>
                      <Paper elevation={0} sx={FLOW_STEP_SX}>
                        <StepOrderHeader
                          order={slot + 2}
                          stepLabel={`${slot + 2}단계`}
                          role={`중간 결재 ${slot + 1}`}
                        />
                        {p ? (
                          <>
                            <Box sx={SETUP_STEP_BODY_SX}>
                              <PersonLine
                                primary={p.name}
                                secondary={p.depNm}
                                primaryTitle={p.name}
                                secondaryTitle={p.depNm}
                              />
                            </Box>
                            <Box sx={STEP_ACTION_SX}>
                              <IconButton
                                size="small"
                                aria-label="중간 결재자 제거"
                                onClick={() => onRemoveIntermediate?.(slot)}
                                sx={{ color: 'text.secondary' }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </>
                        ) : (
                          <>
                            <Box sx={SETUP_STEP_BODY_SX}>
                              <Box
                                role="button"
                                tabIndex={0}
                                onClick={() => onOpenIntermediatePicker?.()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onOpenIntermediatePicker?.();
                                  }
                                }}
                                sx={ADD_APPROVER_BOX_SX}
                              >
                                <AddIcon />
                                <Typography variant="caption" fontWeight={600}>
                                  결재자 추가
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={STEP_ACTION_SX} />
                          </>
                        )}
                      </Paper>
                      <FlowArrowCell />
                    </React.Fragment>
                  );
                })}

                <Paper elevation={0} sx={FLOW_STEP_SX}>
                  <StepOrderHeader order={4} stepLabel="4단계" role="최종 결재" />
                  {finalApprover ? (
                    <>
                      <Box sx={SETUP_STEP_BODY_SX}>
                        <PersonLine
                          primary={finalApprover.name}
                          secondary={finalApprover.depNm}
                          primaryTitle={finalApprover.name}
                          secondaryTitle={finalApprover.depNm}
                        />
                      </Box>
                      <Box sx={STEP_ACTION_SX}>
                        <IconButton
                          color="primary"
                          size="small"
                          aria-label="최종 결재자 선택"
                          onClick={() => onOpenFinalPicker?.()}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box sx={SETUP_STEP_BODY_SX}>
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() => onOpenFinalPicker?.()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onOpenFinalPicker?.();
                            }
                          }}
                          sx={ADD_APPROVER_BOX_SX}
                        >
                          <AddIcon />
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{ px: 0.5, textAlign: 'center' }}
                          >
                            +최종 결재자를 선택하세요
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={STEP_ACTION_SX} />
                    </>
                  )}
                </Paper>
              </FlowGrid>
            </Paper>
          </Box>

          {/* 서명 현황 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
              <VerifiedUserIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                서명 현황
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid #e0e6ed',
                bgcolor: '#fafbfc',
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5, fontWeight: 600 }}
              >
                아래 순서대로 서명이 진행됩니다.
              </Typography>
              <FlowGrid>
                <Paper elevation={0} sx={FLOW_SIGN_STEP_SX}>
                  <StepOrderHeader order={1} stepLabel="1단계" role="신청자" />
                  <Box sx={SETUP_STEP_BODY_SX}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700 }}
                      noWrap
                      title={drafterName}
                    >
                      {drafterName || '—'}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <StatusBadge done={applicantSigned} />
                    </Box>
                  </Box>
                  <Box sx={SIGN_PLACEHOLDER_SX}>
                    {applicantSigned && applicantSignPath ? (
                      <SignImagePreview
                        signPath={applicantSignPath}
                        alt="신청자 서명"
                      />
                    ) : (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        상신 시 서명
                      </Typography>
                    )}
                  </Box>
                </Paper>

                <FlowArrowCell />

                {[0, 1].map((slot) => {
                  const p = intermediateApprovers[slot];
                  const midKey = p?.id != null ? String(p.id) : '';
                  const midSignPath = midSignPathById[midKey] || '';
                  const midSigned =
                    Boolean(midSignedById[midKey]) ||
                    Boolean(String(midSignPath).trim());
                  return (
                    <React.Fragment key={`mid-sign-${slot}`}>
                      <Paper elevation={0} sx={FLOW_SIGN_STEP_SX}>
                        <StepOrderHeader
                          order={slot + 2}
                          stepLabel={`${slot + 2}단계`}
                          role={`중간 결재 ${slot + 1}`}
                        />
                        <SignStatusBody
                          name={p?.name}
                          signed={midSigned}
                          signPath={midSignPath}
                          signLabel={midSigned ? '서명 완료' : '결재 대기'}
                        />
                      </Paper>
                      <FlowArrowCell />
                    </React.Fragment>
                  );
                })}

                <Paper elevation={0} sx={FLOW_SIGN_STEP_SX}>
                  <StepOrderHeader order={4} stepLabel="4단계" role="최종 결재" />
                  <SignStatusBody
                    name={finalApprover?.name}
                    signed={finalSigned}
                    signPath={finalSignPath}
                    signLabel={
                      finalApprover
                        ? finalSigned
                          ? '서명 완료'
                          : '결재 대기'
                        : '미지정'
                    }
                  />
                </Paper>
              </FlowGrid>
            </Paper>
          </Box>

          {/* 하단 버튼 */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              mt: 3,
              pt: 2,
              borderTop: '1px solid #eceff1',
            }}
          >
            <Button
              name="취소"
              variant="outlined"
              onClick={HandleClose}
              sx={{
                minWidth: 88,
                bgcolor: '#fff',
                borderColor: '#cfd8dc',
                color: 'text.primary',
                '&:hover': { bgcolor: '#f5f5f5', borderColor: '#b0bec5' },
              }}
            />
            {applicantSigned && (
              <Button
                name="저장"
                variant="outlined"
                onClick={HandleSaveLine}
                sx={{
                  minWidth: 88,
                  borderColor: '#1565c0',
                  color: '#1565c0',
                  '&:hover': { bgcolor: '#e3f2fd', borderColor: '#0d47a1' },
                }}
              />
            )}
            <Button
              name={submitButtonLabel}
              variant="contained"
              onClick={HandleClickSubmit}
              sx={{
                minWidth: 88,
                bgcolor: '#1565c0',
                color: '#fff',
                '&:hover': { bgcolor: '#0d47a1' },
              }}
            />
          </Box>
            </>
          )}
        </Box>
      }
    />
  );
}

export default TRA_CUSTOM_02_ApprovalModal;
